import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import useHandleApiError from '../../../hooks/useHandleApiError';
import LangEnum from '../../../lang/LangEnum';
import snackbarStore from '../../../stores/Snackbar.store';
import { useAuthContext } from '../../login/AuthContext';
import { useAuthLogin } from '../../login/hook/useAuthLogin';
import { useTranslation } from '../../translation/TranslationContext';
import { useSignUpContext } from '../SignUp.context';
import { signUpRepository } from '../signUp.repository';
import { CreateAccountProps } from '../types/createAccount.types';
import { formatSignUpPayload } from '../utils/formatter/signUpPayloadFormatter';
import {
  CreateAccountFormValues,
  buildCreateAccountValidationSchema,
} from '../utils/validator/createAccount.schema';

export const useCreateAccountForm = ({ store }: CreateAccountProps) => {
  const { state } = useSignUpContext();
  const { doLogin } = useAuthLogin();
  const { translate } = useTranslation();
  const { loginContext } = useAuthContext();
  const { getApiErrorMessage } = useHandleApiError();
  const { handleMessage } = snackbarStore;

  const form = useForm<CreateAccountFormValues>({
    resolver: zodResolver(
      buildCreateAccountValidationSchema({ translate, storeCountryCode: store?.countryCode }),
    ),
    defaultValues: {
      name: '',
      surname: '',
      phone: store?.countryCode ?? '',
      countryCode: store?.countryCode ?? '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const handleChangeCountryCode = (dialCode: string) => form.setValue('countryCode', dialCode);

  const submit = form.handleSubmit(async (data) => {
    const repository = signUpRepository();
    const payload = formatSignUpPayload({
      formData: data,
      storeData: store,
      loginContext,
      state,
    });

    const response = await repository.createAccount(payload);

    if (!response.ok) {
      handleMessage({
        type: 'error',
        message: getApiErrorMessage(response),
      });
      return;
    }

    if (data.email && data.password) {
      await doLogin({
        username: data.email,
        password: data.password,
      });
    }

    handleMessage({
      type: 'success',
      message: translate(LangEnum.CREATE_ACCOUNT_FORM_SUCESS_SUBMIT),
    });
  });

  return {
    form,
    submit,
    handleChangeCountryCode,
    isSubmitting: form.formState.isSubmitting,
  };
};
